package wat.ui

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.*
import wat.i18n.I18n
import wat.i18n.I18n.t
import wat.model.*
import wat.service.DataService
import kotlin.js.Date as JsDate

object WeightTab {
    private var chartRange = try { window.localStorage.getItem("wat_chartRange") ?: "7" } catch (_: Exception) { "7" }

    fun render(container: HTMLDivElement, userId: String, showForm: Boolean, onSave: () -> Unit) {
        // When add form is open, show ONLY the form
        if (showForm) {
            container.appendChild(renderAddForm(userId, onSave))
            return
        }

        val entries = DataService.entries.sortedByDescending { it.date }
        val weightEntries = entries.filter { it.weight > 0 }
        val profile = DataService.profile
        val height = profile.height
        val goalWeight = profile.goalWeight

        if (weightEntries.isNotEmpty()) {
            // Deduplicate: one entry per date (latest wins), sorted newest first
            val byDate = weightEntries.groupBy { it.date }.mapValues { (_, v) -> v.first() }.values.sortedByDescending { it.date }.toList()
            val current = byDate.first()
            val first = byDate.last()
            val prev = if (byDate.size >= 2) byDate[1] else null
            val diff = if (prev != null) current.weight - prev.weight else null
            val bmi = if (height > 0) current.weight / ((height / 100.0) * (height / 100.0)) else 0.0

            // ── Hero: range tabs + weight comparison ──
            // Use same filtered entries as chart for consistency
            val dedupedByDate = weightEntries.sortedBy { it.date }
                .groupBy { it.date }
                .mapValues { (_, v) -> v.last() }
                .values.sortedBy { it.date }
            val chartEntries = filterByRange(dedupedByDate)
            val heroFirst = if (chartEntries.size >= 2) chartEntries.first() else first
            val heroDiff = current.weight - heroFirst.weight
            val isDown = heroDiff < 0
            val isUp = heroDiff > 0
            val col = if (ThemeManager.isDark()) {
                if (isDown) "#2dd4bf" else if (isUp) "#fb7185" else "#818cf8"
            } else {
                if (isDown) "#0a9080" else if (isUp) "#dc3055" else "#5b63d6"
            }
            val absDiff = kotlin.math.abs(heroDiff)
            val diffText = (if (isUp) "+" else if (isDown) "\u2212" else "\u00B1") + fmt1(absDiff)

            // ── Combined card: tabs + hero + chart ──
            container.appendChild(div {
                style.cssText = "background:${Styles.CARD_BG};border:1.5px solid ${Styles.CARD_BORDER};border-radius:18px;overflow:hidden;margin-bottom:28px;position:relative"

                // Glow
                appendChild(div {
                    style.cssText = "position:absolute;left:50%;top:30%;transform:translate(-50%,-50%);width:120px;height:120px;border-radius:60px;background:radial-gradient(circle,${col}06 0%,transparent 70%);pointer-events:none"
                })

                // 1. Range tabs
                appendChild(div {
                    style.cssText = "display:flex;gap:3px;padding:10px 10px 0;position:relative;z-index:1"
                    listOf("1", "7", "14", "30", "90", "all").forEach { r ->
                        val isActive = chartRange == r
                        val label = if (r == "all") "ALL" else r
                        appendChild(button(label) {
                            val activeStyle = if (ThemeManager.isDark()) {
                                "border:1.5px solid ${Styles.CARD_BORDER};background:linear-gradient(145deg,${Styles.TEXT_15},${Styles.TEXT_08});backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);color:${Styles.TEXT};box-shadow:inset 0 1px 0 ${Styles.TEXT_TER},0 2px 10px rgba(0,0,0,0.3)"
                            } else {
                                "border:none;background:#3a3a4a;color:#fff;box-shadow:0 2px 6px rgba(0,0,0,0.08)"
                            }
                            style.cssText = if (isActive) {
                                "flex:1;padding:5px 0;border-radius:8px;$activeStyle;font-size:12px;font-weight:700;letter-spacing:0.5px;font-family:inherit;cursor:pointer"
                            } else {
                                "flex:1;padding:5px 0;border-radius:8px;border:1.5px solid transparent;background:transparent;color:${Styles.TEXT_20};font-size:12px;font-weight:600;letter-spacing:0.5px;font-family:inherit;cursor:pointer"
                            }
                            onclick = {
                                chartRange = r
                                try { window.localStorage.setItem("wat_chartRange", r) } catch (_: Exception) {}
                                MainApp.renderPage()
                                Unit
                            }
                        })
                    }
                })

                // Separator
                appendChild(div { style.cssText = "height:48px" })

                // 2. Weight comparison (horizontal graph)
                appendChild(div {
                    style.cssText = "padding:0 12px;position:relative;z-index:1"

                    // Numbers + line
                    appendChild(div {
                        style.cssText = "display:flex;align-items:center;gap:0"
                        appendChild(div {
                            style.cssText = "display:flex;align-items:baseline;gap:3px"
                            appendChild(span(fmt1(heroFirst.weight)) {
                                style.cssText = "font-size:clamp(26px,7vw,32px);font-weight:800;color:${Styles.TEXT_35};line-height:1"
                            })
                            appendChild(span("kg") {
                                style.cssText = "font-size:10px;font-weight:500;color:${Styles.TEXT_TER}"
                            })
                        })
                        appendChild(div {
                            style.cssText = "flex:1;display:flex;align-items:center;margin:0 6px"
                            appendChild(div { style.cssText = "flex:1;height:3px;background:linear-gradient(90deg,${Styles.TEXT_15},${col}80);border-radius:2px" })
                            appendChild(div {
                                style.cssText = "padding:3px 10px;border-radius:14px;background:${col}${if (ThemeManager.isDark()) "15" else "20"};border:1.5px solid ${col}${if (ThemeManager.isDark()) "25" else "40"};margin:0 2px"
                                appendChild(span(diffText) { style.cssText = "font-size:13px;font-weight:800;color:$col;letter-spacing:-0.5px" })
                            })
                            appendChild(div { style.cssText = "flex:1;height:3px;background:linear-gradient(90deg,${col}60,${col}80);border-radius:2px" })
                            appendChild(div {
                                style.cssText = "flex-shrink:0;margin-left:3px;display:flex;align-items:center"
                                val arrowSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
                                arrowSvg.setAttribute("width", "24"); arrowSvg.setAttribute("height", "24"); arrowSvg.setAttribute("viewBox", "0 0 24 24")
                                arrowSvg.innerHTML = "<path d='M5 7l5 5-5 5' stroke='$col' stroke-width='3' stroke-linecap='round' stroke-linejoin='round' fill='none' opacity='0.5'/><path d='M13 7l5 5-5 5' stroke='$col' stroke-width='3' stroke-linecap='round' stroke-linejoin='round' fill='none'/>"
                                appendChild(arrowSvg)
                            })
                        })
                        appendChild(div {
                            style.cssText = "display:flex;align-items:baseline;gap:3px;cursor:pointer"
                            appendChild(span(fmt1(current.weight)) { style.cssText = "font-size:clamp(26px,7vw,32px);font-weight:800;color:${Styles.TEXT};line-height:1;letter-spacing:-1px" })
                            appendChild(span("kg") { style.cssText = "font-size:12px;font-weight:500;color:${Styles.TEXT_70}" })
                            onclick = { MainApp.showAddForm = true; MainApp.renderPage(); Unit }
                        })
                    })
                    // Dates + share
                    appendChild(div {
                        style.cssText = "display:flex;justify-content:space-between;align-items:center;margin-top:4px"
                        appendChild(span(formatDateFull(heroFirst.date)) { style.cssText = "font-size:10px;font-weight:500;color:${Styles.YELLOW}" })
                        appendChild(ShareCard.shareButton {
                            ShareCard.shareWeight(current.weight, diff, goalWeight, bmi, current.date)
                        })
                        appendChild(span(formatDateFull(current.date)) { style.cssText = "font-size:10px;font-weight:500;color:${Styles.YELLOW}" })
                    })
                })

                // Separator
                appendChild(div { style.cssText = "height:0px" })

                // 3. SVG Chart
                if (chartEntries.isNotEmpty()) {
                    appendChild(renderChartSvg(chartEntries, goalWeight, userId, col))
                }
            })

            // ── BMI bar ──
            if (bmi > 0) {
                container.appendChild(renderBmiPanel(bmi, height, current.weight))
            }

            // ── Goal progress ──
            if (goalWeight > 0 && weightEntries.size >= 1) {
                container.appendChild(renderGoalPanel(current.weight, goalWeight, first.weight, userId))
            }
        }
    }

    // ── Range selector ──────────────────────────────────────────────────

    private fun renderRangeSelector(): HTMLElement {
        val ranges = listOf("7", "14", "30", "90", "all")
        return div {
            style.cssText = "display:flex;gap:4px;margin-bottom:20px"
            ranges.forEach { r ->
                val isActive = chartRange == r
                val label = if (r == "all") "ALL" else r
                appendChild(button(label) {
                    style.cssText = buildString {
                        append("flex:1;padding:8px 0;border-radius:8px;border:none;cursor:pointer;font-size:13px;font-weight:700;letter-spacing:0.5px;font-family:inherit;transition:all 0.15s;")
                        if (isActive) {
                            append("background:${Styles.A1};color:#000;")
                        } else {
                            append("background:${Styles.TEXT_05};color:${Styles.TEXT_30};")
                        }
                    }
                    onclick = {
                        chartRange = r
                        try { window.localStorage.setItem("wat_chartRange", r) } catch (_: Exception) {}
                        MainApp.renderPage()
                        Unit
                    }
                })
            }
        }
    }

    private fun filterByRange(sorted: List<Entry>): List<Entry> {
        if (chartRange == "all" || sorted.isEmpty()) return sorted
        val count = when (chartRange) {
            "1" -> 2
            "7" -> 7
            "14" -> 14
            "30" -> 30
            "90" -> 90
            else -> Int.MAX_VALUE
        }
        return sorted.takeLast(count)
    }

    // ── Stat card ───────────────────────────────────────────────────────

    private fun statCard(label: String, value: String, unit: String, color: String): HTMLElement {
        return div {
            style.cssText = "background:${Styles.TEXT_05};border:1.5px solid ${Styles.CARD_BORDER};border-radius:16px;padding:clamp(12px,2vw,20px);flex:1 1 88px;min-width:88px"
            appendChild(div {
                style.cssText = "font-size:13px;text-transform:uppercase;letter-spacing:2px;color:${Styles.TEXT_40};font-weight:700;margin-bottom:6px"
                textContent = label
            })
            appendChild(div {
                style.cssText = "display:flex;align-items:baseline;gap:3px"
                appendChild(span(value) {
                    style.cssText = "font-size:clamp(22px,5.5vw,34px);font-weight:800;color:$color;line-height:1"
                })
                if (unit.isNotBlank()) {
                    appendChild(span(unit) {
                        style.cssText = "font-size:13px;color:${Styles.TEXT_40};font-weight:700"
                    })
                }
            })
        }
    }

    // ── SVG Chart ────────────────────────────────────────────────────────

    private fun renderChartSvg(deduped: List<Entry>, goal: Double, userId: String, trendColor: String = Styles.A1): HTMLElement {
        if (deduped.isEmpty()) return div {}

        val singlePoint = deduped.size == 1
        val W = 480; val H = 340
        val pL = 46; val pR = 16; val pT = 44; val pB = 36
        val cW = W - pL - pR; val cH = H - pT - pB

        val today = todayLocal()
        val firstDate = deduped.first().date
        val totalDays = if (singlePoint) 1 else daysBetween(firstDate, today).coerceAtLeast(1)

        val wts = deduped.map { it.weight }
        val wMin = wts.min()
        val wMax = wts.max()
        val visMin = if (goal > 0) minOf(wMin, goal) else wMin
        val visMax = if (goal > 0) maxOf(wMax, goal) else wMax
        val wPad = (visMax - visMin) * 0.15 + 0.5
        val yMin = visMin - wPad
        val yMax = visMax + wPad
        val yRange = (yMax - yMin).coerceAtLeast(0.1)

        val step = when {
            yRange <= 3 -> 0.5
            yRange <= 7 -> 1.0
            yRange <= 15 -> 2.0
            yRange <= 35 -> 5.0
            else -> 10.0
        }

        fun yPos(w: Double) = pT + cH - ((w - yMin) / yRange) * cH
        fun xPos(date: String): Double {
            val days = daysBetween(firstDate, date)
            return pL + (days.toDouble() / totalDays) * cW
        }

        val pts = deduped.map { e ->
            val x = if (singlePoint) (pL + cW / 2.0) else xPos(e.date)
            Triple(x, yPos(e.weight), e)
        }

        // Catmull-Rom spline
        var path = "M${pts[0].first} ${pts[0].second}"
        if (!singlePoint) {
            for (i in 1 until pts.size) {
                val p0 = if (i >= 2) pts[i - 2] else pts[i - 1]
                val p1 = pts[i - 1]; val p2 = pts[i]
                val p3 = if (i + 1 < pts.size) pts[i + 1] else pts[i]
                val t = 6.0
                path += " C${p1.first + (p2.first - p0.first) / t} ${p1.second + (p2.second - p0.second) / t},${p2.first - (p3.first - p1.first) / t} ${p2.second - (p3.second - p1.second) / t},${p2.first} ${p2.second}"
            }
        }
        val area = if (singlePoint) null
            else path + " L${pts.last().first} ${pT + cH} L${pts.first().first} ${pT + cH} Z"

        // Y grid
        val yLabels = mutableListOf<Double>()
        var yv = kotlin.math.ceil(yMin / step) * step
        while (yv <= yMax) { yLabels.add(kotlin.math.round(yv * 10) / 10.0); yv += step }

        // X labels
        val numXLabels = when {
            totalDays <= 7 -> totalDays + 1
            totalDays <= 14 -> 7
            totalDays <= 30 -> 6
            else -> 7
        }.coerceAtLeast(2)

        val firstParts = firstDate.split("-")
        val baseYear = firstParts[0].toInt()
        val baseMonth = firstParts[1].toInt() - 1
        val baseDay = firstParts[2].toInt()

        val lastPt = pts.last()
        val lastWeight = lastPt.third.weight

        val svgContent = buildString {
            // Defs - clean, no glow filters
            append("<defs>")
            append("<linearGradient id='areaG' x1='0' y1='0' x2='0' y2='1'>")
            append("<stop offset='0%' stop-color='${trendColor}' stop-opacity='${if (ThemeManager.isDark()) "0.12" else "0.2"}'/>")
            append("<stop offset='100%' stop-color='${trendColor}' stop-opacity='0'/>")
            append("</linearGradient>")
            append("</defs>")

            // Horizontal grid + Y-axis labels
            yLabels.forEach { v ->
                val y = yPos(v)
                if (y >= pT - 2 && y <= pT + cH + 2) {
                    append("<line x1='$pL' y1='$y' x2='${W - pR}' y2='$y' stroke='${Styles.GRID_LINE_DASH}' stroke-width='0.5' stroke-dasharray='3 4'/>")
                    val label = if (step >= 1.0) "${v.toInt()}" else fmt1(v)
                    append("<text x='${pL - 6}' y='${y + 4}' text-anchor='end' fill='${Styles.TEXT_45}' font-size='12' font-weight='700' font-family='system-ui'>$label</text>")
                }
            }

            // Bottom axis
            append("<line x1='$pL' y1='${pT + cH}' x2='${W - pR}' y2='${pT + cH}' stroke='${Styles.GRID_LINE}' stroke-width='0.5'/>")

            // X-axis date labels
            if (!singlePoint) {
                val dayStep = totalDays.toDouble() / (numXLabels - 1).coerceAtLeast(1)
                for (i in 0 until numXLabels) {
                    val dayOff = (i * dayStep).toInt().coerceAtMost(totalDays)
                    val x = pL + (dayOff.toDouble() / totalDays) * cW
                    val d = JsDate(baseYear, baseMonth, baseDay + dayOff)
                    val lbl = "${d.getDate()}.${d.getMonth() + 1}."
                    val anchor = when { i == 0 -> "start"; i == numXLabels - 1 -> "end"; else -> "middle" }
                    append("<text x='$x' y='${pT + cH + 16}' text-anchor='$anchor' fill='${Styles.TEXT_40}' font-size='11' font-weight='600' font-family='system-ui'>$lbl</text>")
                }
            }

            // Area fill
            if (area != null) {
                append("<path d='$area' fill='url(#areaG)'/>")
            }

            // Main curve - clean solid line, no effects
            if (singlePoint) {
                val y = pts[0].second
                append("<line x1='$pL' y1='$y' x2='${W - pR}' y2='$y' stroke='${trendColor}' stroke-width='1.5' stroke-dasharray='4 3' stroke-opacity='0.3'/>")
            } else {
                append("<path d='$path' fill='none' stroke='${trendColor}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/>")
            }

            // Data points
            pts.forEachIndexed { i, (x, y, e) ->
                val isLast = i == pts.size - 1
                val isFirst = i == 0
                if (!isLast && !isFirst && !singlePoint) {
                    if (pts.size <= 50) {
                        val r = if (pts.size > 25) 1.5 else 2.5
                        append("<circle cx='$x' cy='$y' r='$r' fill='${trendColor}' fill-opacity='${if (ThemeManager.isDark()) "0.4" else "0.7"}'/>")
                    }
                    if (pts.size <= 7) {
                        append("<text x='$x' y='${y - 8}' text-anchor='middle' fill='${Styles.TEXT_40}' font-size='10' font-weight='700'>${fmt1(e.weight)}</text>")
                    }
                }
                // First point
                if (isFirst && !singlePoint) {
                    append("<circle cx='$x' cy='$y' r='3.5' fill='${Styles.TEXT_40}'/>")
                    val fy = (y - 10).coerceAtLeast(pT + 2.0)
                    append("<text x='${x + 6}' y='$fy' text-anchor='start' fill='${Styles.TEXT_40}' font-size='11' font-weight='700' font-family='system-ui'>${fmt1(e.weight)}</text>")
                }
            }

            // ── Goal line (interactive) ──
            if (goal > 0) {
                val gY = yPos(goal)
                val goalText = "${t("chart_goal_badge")} ${fmt1(goal)}"
                val lblW = 72
                // Dashed line — starts AFTER label so it doesn't show behind it
                append("<line x1='$pL' y1='$gY' x2='${W - pR}' y2='$gY' stroke='${Styles.YELLOW}' stroke-width='3' stroke-opacity='0.04'/>")
                append("<line id='wg-goal-line' x1='${pL + lblW + 6}' y1='$gY' x2='${W - pR}' y2='$gY' stroke='${Styles.YELLOW}' stroke-width='1.5' stroke-dasharray='5 4' stroke-opacity='${if (ThemeManager.isDark()) "0.6" else "1"}'/>")
                // Label pill — opaque background so nothing shows through
                append("<rect id='wg-goal-rect' x='$pL' y='${gY - 11}' width='$lblW' height='22' rx='11' fill='${Styles.BG_DARK}' stroke='${Styles.YELLOW}50' stroke-width='1'/>")
                append("<rect x='$pL' y='${gY - 11}' width='$lblW' height='22' rx='11' fill='${Styles.YELLOW}20'/>")
                append("<text id='wg-goal-text' x='${pL + lblW / 2}' y='${gY + 4}' text-anchor='middle' fill='${Styles.YELLOW}' font-size='11' font-weight='800' font-family='system-ui'>$goalText</text>")
                // Drag handle
                append("<circle id='wg-goal-handle' cx='${W - pR}' cy='$gY' r='7' fill='${Styles.YELLOW}40' stroke='${Styles.YELLOW}' stroke-width='1.5' style='cursor:ns-resize'/>")
                append("<line id='wg-goal-grip1' x1='${W - pR - 3}' y1='${gY - 2}' x2='${W - pR + 3}' y2='${gY - 2}' stroke='${Styles.YELLOW}' stroke-width='1' stroke-opacity='0.7'/>")
                append("<line id='wg-goal-grip2' x1='${W - pR - 3}' y1='${gY + 2}' x2='${W - pR + 3}' y2='${gY + 2}' stroke='${Styles.YELLOW}' stroke-width='1' stroke-opacity='0.7'/>")
                append("<rect x='${pL - 5}' y='${gY - 16}' width='${cW + 10}' height='32' fill='transparent' style='cursor:ns-resize' class='goal-hit'/>")
            }

            // ── Today marker ──
            val todayPt = pts.find { it.third.date == today }
            val tx = todayPt?.first ?: (W - pR).toDouble()
            append("<line x1='$tx' y1='$pT' x2='$tx' y2='${pT + cH}' stroke='${Styles.adaptColor("#a78bfa")}' stroke-width='1' stroke-opacity='${if (ThemeManager.isDark()) "0.25" else "0.5"}' stroke-dasharray='3 3'/>")

            // ── Current weight — clean dot + label ──
            val lx = lastPt.first
            val ly = lastPt.second
            append("<circle cx='$lx' cy='$ly' r='5' fill='${trendColor}'/>")
            // Label above
            val lastLabelY = (ly - 12).coerceAtLeast(pT + 2.0)
            val lastAnchor = if (lx > W - pR - 30) "end" else "middle"
            append("<text x='$lx' y='$lastLabelY' text-anchor='$lastAnchor' fill='${Styles.TEXT}' font-size='13' font-weight='900' font-family='system-ui'>${fmt1(lastWeight)}</text>")
        }

        return div {
            val svgEl = document.createElementNS("http://www.w3.org/2000/svg", "svg") as Element
            svgEl.setAttribute("viewBox", "0 0 $W $H")
            svgEl.setAttribute("style", "width:100%;display:block;touch-action:none")
            svgEl.innerHTML = svgContent
            appendChild(svgEl)

            // ── Goal drag: tap to unlock, drag to move, release to lock+save ──
            if (goal > 0) {
                var unlocked = false
                var dragging = false
                var currentGoalWeight = goal

                fun setGoalHighlight(on: Boolean) {
                    val op = if (on) "1" else "0.5"
                    val sw = if (on) "3" else "1.5"
                    val hc = if (on) Styles.YELLOW else "${Styles.YELLOW}40"
                    svgEl.querySelector("#wg-goal-line")?.setAttribute("stroke-opacity", op)
                    svgEl.querySelector("#wg-goal-line")?.setAttribute("stroke-width", sw)
                    svgEl.querySelector("#wg-goal-handle")?.setAttribute("fill", if (on) Styles.YELLOW else "${Styles.YELLOW}40")
                    svgEl.querySelector("#wg-goal-handle")?.setAttribute("r", if (on) "10" else "8")
                    svgEl.querySelector("#wg-goal-rect")?.setAttribute("stroke", if (on) Styles.YELLOW else "${Styles.YELLOW}50")
                }

                svgEl.addEventListener("pointerdown", { evt ->
                    val e = evt.asDynamic()
                    val rect = svgEl.asDynamic().getBoundingClientRect()
                    val sy = ((e.clientY as Double) - (rect.top as Double)) / (rect.height as Double) * H
                    val goalY = yPos(currentGoalWeight)
                    if (kotlin.math.abs(sy - goalY) < 30) {
                        if (!unlocked) {
                            // First tap: unlock
                            unlocked = true
                            setGoalHighlight(true)
                        } else {
                            // Second touch: start dragging
                            dragging = true
                            svgEl.asDynamic().style.touchAction = "none"
                            svgEl.asDynamic().setPointerCapture(e.pointerId)
                            evt.preventDefault()
                        }
                    } else if (unlocked && !dragging) {
                        // Tapped elsewhere while unlocked — lock back
                        unlocked = false
                        setGoalHighlight(false)
                    }
                })

                svgEl.addEventListener("pointermove", { evt ->
                    if (dragging) {
                        val e = evt.asDynamic()
                        val rect = svgEl.asDynamic().getBoundingClientRect()
                        val sy = ((e.clientY as Double) - (rect.top as Double)) / (rect.height as Double) * H
                        val newW = yMin + (1.0 - (sy - pT) / cH) * yRange
                        currentGoalWeight = (kotlin.math.round(newW * 10.0) / 10.0).coerceIn(20.0, 300.0)
                        val newY = yPos(currentGoalWeight)

                        svgEl.querySelector("#wg-goal-line")?.let { it.setAttribute("y1", "$newY"); it.setAttribute("y2", "$newY") }
                        svgEl.querySelector("#wg-goal-rect")?.setAttribute("y", "${newY - 11}")
                        svgEl.querySelector("#wg-goal-text")?.let { it.setAttribute("y", "${newY + 4}"); it.textContent = "${t("chart_goal_badge")} ${fmt1(currentGoalWeight)}" }
                        svgEl.querySelector("#wg-goal-rect")?.nextElementSibling?.let { it.setAttribute("y", "${newY - 11}") }
                        svgEl.querySelector("#wg-goal-handle")?.setAttribute("cy", "$newY")
                        svgEl.querySelector("#wg-goal-grip1")?.let { it.setAttribute("y1", "${newY - 2}"); it.setAttribute("y2", "${newY - 2}") }
                        svgEl.querySelector("#wg-goal-grip2")?.let { it.setAttribute("y1", "${newY + 2}"); it.setAttribute("y2", "${newY + 2}") }
                        svgEl.querySelector(".goal-hit")?.setAttribute("y", "${newY - 18}")
                        evt.preventDefault()
                    }
                })

                svgEl.addEventListener("pointerup", { _ ->
                    if (dragging) {
                        dragging = false
                        unlocked = false
                        setGoalHighlight(false)
                        svgEl.asDynamic().style.touchAction = ""
                        if (currentGoalWeight != goal && currentGoalWeight > 0) {
                            GlobalScope.launch {
                                val p = DataService.profile.copy(goalWeight = currentGoalWeight)
                                DataService.saveProfile(userId, p)
                                DataService.profile = p
                                MainApp.renderPage()
                            }
                        }
                    }
                })

                svgEl.addEventListener("pointercancel", { _ ->
                    dragging = false
                    unlocked = false
                    setGoalHighlight(false)
                    svgEl.asDynamic().style.touchAction = ""
                })
            }

            // ── Inline goal editor below chart ──
            appendChild(div {
                style.cssText = "display:flex;align-items:center;gap:8px;padding:10px 14px;border-top:1px solid ${Styles.TEXT_05}"
                appendChild(span("\u25CE") { style.cssText = "font-size:14px;color:${Styles.YELLOW}" })
                appendChild(span(t("chart_goal_badge")) {
                    style.cssText = "font-size:12px;font-weight:700;color:${Styles.YELLOW};letter-spacing:1px"
                })
                val goalInput = input("number") {
                    style.cssText = "width:72px;background:${Styles.TEXT_06};border:1.5px solid ${Styles.YELLOW}25;border-radius:8px;padding:6px 8px;color:${Styles.TEXT};font-size:15px;font-weight:800;text-align:center;outline:none;font-family:inherit;-moz-appearance:textfield"
                    value = if (goal > 0) fmt1(goal) else ""
                    placeholder = "0.0"
                    asDynamic().step = "0.1"
                }
                appendChild(goalInput)
                appendChild(span("kg") {
                    style.cssText = "font-size:11px;color:${Styles.TEXT_30};font-weight:600"
                })
                appendChild(button("\u2713") {
                    style.cssText = "width:28px;height:28px;border-radius:8px;border:none;background:${Styles.YELLOW}20;color:${Styles.YELLOW};font-size:14px;font-weight:800;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0"
                    onclick = {
                        val nv = goalInput.value.toDoubleOrNull() ?: 0.0
                        if (nv in 20.0..300.0) {
                            GlobalScope.launch {
                                val p = DataService.profile.copy(goalWeight = nv)
                                DataService.saveProfile(userId, p)
                                DataService.profile = p
                                DataService.saveProfileToLocal(p)
                                MainApp.renderPage()
                            }
                        }
                        Unit
                    }
                })
                if (goal > 0) {
                    appendChild(button("\u2715") {
                        style.cssText = "width:28px;height:28px;border-radius:8px;border:none;background:#fb718510;color:#fb7185;font-size:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0"
                        onclick = {
                            GlobalScope.launch {
                                val p = DataService.profile.copy(goalWeight = 0.0)
                                DataService.saveProfile(userId, p)
                                DataService.profile = p
                                DataService.saveProfileToLocal(p)
                                MainApp.renderPage()
                            }
                            Unit
                        }
                    })
                }
            })
        }
    }

    // ── BMI Panel ───────────────────────────────────────────────────────

    private fun renderBmiPanel(bmi: Double, height: Int, weight: Double): HTMLElement {
        val info = getBmiInfo(bmi)
        val zones = if (DataService.bmiZones.isNotEmpty()) {
            DataService.bmiZones.map { z -> listOf(z.minBmi, z.maxBmi, Styles.adaptColor(z.color), t(z.translationKey)) }
        } else {
            listOf(
                listOf(12.0, 18.5, Styles.adaptColor("#38bdf8"), t("bmi_zone_underweight")),
                listOf(18.5, 25.0, Styles.adaptColor("#2dd4bf"), t("bmi_zone_normal")),
                listOf(25.0, 30.0, Styles.YELLOW, t("bmi_zone_overweight")),
                listOf(30.0, 42.0, Styles.adaptColor("#fb7185"), t("bmi_zone_obese"))
            )
        }
        val hm = height / 100.0
        fun toKg(b: Double) = fmt0(b * hm * hm)
        fun pct(v: Double) = ((v.coerceIn(12.0, 42.0) - 12.0) / 30.0 * 100).coerceIn(0.0, 100.0)

        return div {
            style.cssText = "${Styles.CARD};padding:clamp(24px,5vw,32px)"

            // Value + label row
            appendChild(div {
                style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:24px"
                // Left: big number
                appendChild(div {
                    style.cssText = "display:flex;align-items:baseline;gap:6px"
                    appendChild(span(fmt1(bmi)) {
                        style.cssText = "font-size:clamp(26px,7vw,32px);font-weight:800;color:${info.second};line-height:1;letter-spacing:-1px"
                    })
                    appendChild(span("BMI") {
                        style.cssText = "font-size:12px;font-weight:600;color:${info.second}50"
                    })
                })
                // Right: zone badge
                appendChild(div {
                    val badgeBgAlpha = if (ThemeManager.isDark()) "12" else "18"
                    style.cssText = "padding:8px 20px;border-radius:24px;background:${info.second}$badgeBgAlpha"
                    appendChild(span(info.first) {
                        style.cssText = "font-size:15px;font-weight:700;color:${info.second}"
                    })
                })
            })

            // Color bar with indicator - vivid
            appendChild(div {
                style.cssText = "position:relative;margin-bottom:14px"
                appendChild(div {
                    style.cssText = "display:flex;border-radius:7px;overflow:hidden;height:14px"
                    zones.forEach { z ->
                        val f = z[0] as Double; val tt = z[1] as Double; val c = z[2] as String
                        val barAlpha = if (ThemeManager.isDark()) "70" else "90"
                        appendChild(div { style.cssText = "flex:${((tt - f) * 10).toInt()};background:${c}$barAlpha" })
                    }
                })
                appendChild(div {
                    style.cssText = "position:absolute;left:calc(${pct(bmi)}% - 3px);top:-4px;width:6px;height:22px;border-radius:3px;background:${Styles.TEXT};box-shadow:0 0 12px ${info.second},0 2px 6px rgba(0,0,0,${if (ThemeManager.isDark()) "0.4" else "0.15"})"
                })
            })

            // Zone labels with kg ranges
            appendChild(div {
                style.cssText = "display:flex"
                zones.forEach { z ->
                    val f = z[0] as Double; val tt = z[1] as Double; val c = z[2] as String; val lbl = z[3] as String
                    appendChild(div {
                        style.cssText = "flex:${((tt - f) * 10).toInt()};text-align:center"
                        appendChild(div { style.cssText = "font-size:12px;font-weight:800;color:$c"; textContent = lbl })
                        appendChild(div { style.cssText = "font-size:10px;font-weight:600;color:${Styles.TEXT_TER};margin-top:3px"; textContent = "${toKg(f)}-${toKg(tt)} kg" })
                    })
                }
            })
        }
    }

    // ── Goal Panel ──────────────────────────────────────────────────────

    private fun renderGoalPanel(current: Double, goal: Double, start: Double, userId: String): HTMLElement {
        val tot = kotlin.math.abs(start - goal)
        val done = kotlin.math.abs(start - current)
        val pct = if (tot > 0) (done / tot * 100).coerceIn(0.0, 100.0) else 0.0
        val rem = kotlin.math.abs(current - goal)
        val lost = kotlin.math.abs(start - current)
        val isGaining = current > start
        val col = if (pct >= 100) Styles.A1 else Styles.YELLOW

        return div {
            style.cssText = "${Styles.CARD};padding:clamp(24px,5vw,32px)"

            // Value + label row - same layout as BMI
            appendChild(div {
                style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:24px"
                // Left: big percentage
                appendChild(div {
                    style.cssText = "display:flex;align-items:baseline;gap:4px"
                    appendChild(span("${pct.toInt()}") {
                        style.cssText = "font-size:clamp(26px,7vw,32px);font-weight:800;color:$col;line-height:1;letter-spacing:-1px"
                    })
                    appendChild(span("%") {
                        style.cssText = "font-size:clamp(14px,4vw,18px);font-weight:600;color:${col}50"
                    })
                })
                // Right: goal label
                appendChild(div {
                    style.cssText = "padding:8px 20px;border-radius:24px;background:${col}12"
                    appendChild(span(t("goal_title")) {
                        style.cssText = "font-size:15px;font-weight:700;color:$col"
                    })
                })
            })

            // Progress bar
            appendChild(div {
                style.cssText = "height:10px;border-radius:5px;background:${Styles.TEXT_06};overflow:hidden;margin-bottom:24px"
                appendChild(div {
                    style.cssText = "height:100%;width:${pct.coerceAtLeast(2.0)}%;border-radius:5px;background:$col"
                })
            })

            // Start → Current → Goal row
            appendChild(div {
                style.cssText = "display:flex;align-items:center;margin-bottom:20px"

                // Start
                appendChild(div {
                    style.cssText = "text-align:center"
                    appendChild(div { style.cssText = "font-size:10px;font-weight:600;color:${Styles.TEXT_TER};margin-bottom:4px"; textContent = t("goal_start") })
                    appendChild(div { style.cssText = "font-size:18px;font-weight:700;color:${Styles.TEXT_40}"; textContent = fmt1(start) })
                })

                // Arrow
                appendChild(div {
                    style.cssText = "flex:1;display:flex;align-items:center;justify-content:center;padding:0 8px"
                    innerHTML = "<svg width='24' height='12' viewBox='0 0 24 12' fill='none'><path d='M0 6h20m0 0l-4-4m4 4l-4 4' stroke='${Styles.TEXT_TER}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>"
                })

                // Current
                appendChild(div {
                    style.cssText = "text-align:center"
                    appendChild(div { style.cssText = "font-size:10px;font-weight:600;color:${Styles.TEXT_TER};margin-bottom:4px"; textContent = t("goal_current") })
                    appendChild(div { style.cssText = "font-size:22px;font-weight:900;color:${Styles.TEXT}"; textContent = fmt1(current) })
                })

                // Arrow
                appendChild(div {
                    style.cssText = "flex:1;display:flex;align-items:center;justify-content:center;padding:0 8px"
                    innerHTML = "<svg width='24' height='12' viewBox='0 0 24 12' fill='none'><path d='M0 6h20m0 0l-4-4m4 4l-4 4' stroke='${Styles.TEXT_TER}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>"
                })

                // Goal (editable)
                appendChild(div {
                    style.cssText = "text-align:center"
                    appendChild(div { style.cssText = "font-size:10px;font-weight:600;color:${Styles.TEXT_TER};margin-bottom:4px"; textContent = t("goal_target") })

                    val displayDiv = div {
                        style.cssText = "cursor:pointer"
                        appendChild(div {
                            style.cssText = "display:flex;align-items:baseline;justify-content:center;gap:2px"
                            appendChild(span(fmt1(goal)) { style.cssText = "font-size:22px;font-weight:900;color:$col" })
                            appendChild(span("\u270E") { style.cssText = "font-size:9px;color:${Styles.TEXT_15};margin-left:4px" })
                        })
                    }
                    val goalInput = input("number") {
                        style.cssText = "width:72px;background:${Styles.CARD_BG};border:none;border-radius:8px;padding:6px;color:${Styles.TEXT};font-size:18px;font-weight:800;text-align:center;outline:none;font-family:inherit"
                        value = fmt1(goal)
                        step = "0.1"
                    }
                    val saveBtn = button("\u2713") {
                        style.cssText = "width:26px;height:26px;border-radius:13px;border:none;background:$col;color:#000;font-size:12px;font-weight:800;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0"
                        onclick = {
                            val nv = goalInput.value.toDoubleOrNull() ?: 0.0
                            if (nv > 0) {
                                GlobalScope.launch {
                                    val p = DataService.profile.copy(goalWeight = nv)
                                    DataService.saveProfile(userId, p)
                                    DataService.profile = p
                                    MainApp.renderPage()
                                }
                            }
                            Unit
                        }
                    }
                    val cancelBtn = button("\u2715") {
                        style.cssText = "width:26px;height:26px;border-radius:13px;border:none;background:${Styles.TEXT_06};color:${Styles.TEXT_30};font-size:10px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0"
                    }
                    val editDiv = div {
                        style.cssText = "display:none;flex-direction:column;align-items:center;gap:6px"
                        appendChild(goalInput)
                        appendChild(div {
                            style.cssText = "display:flex;gap:4px"
                            appendChild(saveBtn)
                            appendChild(cancelBtn)
                        })
                    }
                    cancelBtn.onclick = { displayDiv.style.display = ""; editDiv.style.display = "none"; Unit }
                    displayDiv.onclick = { displayDiv.style.display = "none"; editDiv.style.display = "flex"; Unit }
                    appendChild(displayDiv)
                    appendChild(editDiv)
                })
            })

            // Bottom stats: lost + remaining
            appendChild(div {
                style.cssText = "display:flex;gap:12px"
                val lostColor = if (!isGaining) Styles.A1 else "#fb7185"
                val lostLabel = if (!isGaining) t("goal_lost") else t("goal_gained")

                appendChild(div {
                    style.cssText = "flex:1;text-align:center;padding:14px 8px;background:${Styles.TEXT_04};border-radius:12px"
                    appendChild(div { style.cssText = "font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:${Styles.TEXT_TER};margin-bottom:6px"; textContent = lostLabel })
                    appendChild(div {
                        style.cssText = "display:flex;align-items:baseline;justify-content:center;gap:3px"
                        appendChild(span(fmt1(lost)) { style.cssText = "font-size:20px;font-weight:800;color:$lostColor" })
                        appendChild(span("kg") { style.cssText = "font-size:11px;font-weight:500;color:${Styles.TEXT_20}" })
                    })
                })

                appendChild(div {
                    style.cssText = "flex:1;text-align:center;padding:14px 8px;background:${Styles.TEXT_04};border-radius:12px"
                    appendChild(div { style.cssText = "font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:${Styles.TEXT_TER};margin-bottom:6px"; textContent = t("goal_remaining") })
                    appendChild(div {
                        style.cssText = "display:flex;align-items:baseline;justify-content:center;gap:3px"
                        appendChild(span(fmt1(rem)) { style.cssText = "font-size:20px;font-weight:800;color:${if (rem < 1) Styles.A1 else col}" })
                        appendChild(span("kg") { style.cssText = "font-size:11px;font-weight:500;color:${Styles.TEXT_20}" })
                    })
                })
            })
        }
    }

    // ── Add Weight Form ─────────────────────────────────────────────────

    private fun renderAddForm(userId: String, onSave: () -> Unit): HTMLElement {
        var formDate = todayLocal()
        var formWeight = ""
        var formNote = ""

        val INP = "background:${Styles.TEXT_06};border:none;border-radius:10px;padding:12px 14px;color:${Styles.TEXT};font-size:16px;font-family:inherit;outline:none;width:100%;box-sizing:border-box;font-weight:700"

        return div {
            style.cssText = Styles.CARD

            // Title - same as "Tento týždeň"
            appendChild(div {
                style.cssText = "font-size:15px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:${Styles.TEXT_40};margin-bottom:18px"
                textContent = t("weight_add")
            })

            // Weight - big centered input + validation
            val weightError = div { style.cssText = "display:none;text-align:center;margin-top:6px;font-size:12px;font-weight:600;color:#fb7185" }
            appendChild(div {
                style.cssText = "text-align:center;margin-bottom:20px"
                appendChild(div {
                    style.cssText = "display:flex;align-items:center;justify-content:center;gap:8px"
                    appendChild(input("number") {
                        asDynamic().step = "0.1"
                        style.cssText = "width:140px;background:${Styles.TEXT_06};border:none;border-radius:12px;padding:14px;color:${Styles.TEXT};font-size:32px;font-weight:900;text-align:center;outline:none;font-family:inherit;-moz-appearance:textfield"
                        placeholder = "0.0"
                        oninput = { e ->
                            formWeight = (e.target as HTMLInputElement).value
                            val w = formWeight.toDoubleOrNull()
                            if (w != null && w > 0 && w !in 20.0..300.0) {
                                weightError.textContent = t("onboard_err_weight")
                                weightError.style.display = "block"
                            } else {
                                weightError.style.display = "none"
                            }
                            Unit
                        }
                    })
                    appendChild(span("kg") {
                        style.cssText = "font-size:22px;font-weight:700;color:${Styles.TEXT_TER}"
                    })
                })
                appendChild(weightError)
            })

            // Date + Note row
            appendChild(div {
                style.cssText = "display:grid;grid-template-columns:auto 1fr;gap:10px;margin-bottom:20px"
                appendChild(input("date") {
                    style.cssText = "$INP;color-scheme:${Styles.COLOR_SCHEME};width:auto"
                    value = formDate
                    max = todayLocal()
                    oninput = { e -> formDate = (e.target as HTMLInputElement).value; Unit }
                })
                appendChild(input("text") {
                    style.cssText = INP
                    placeholder = t("label_note")
                    oninput = { e -> formNote = (e.target as HTMLInputElement).value; Unit }
                })
            })

            // Buttons
            appendChild(div {
                style.cssText = "display:flex;gap:10px;padding-top:16px;margin-top:4px"
                appendChild(button(t("btn_save")) {
                    style.cssText = "flex:1;padding:14px;border-radius:12px;border:none;${Styles.BTN_ACTION};font-size:15px;font-weight:800;cursor:pointer"
                    onclick = {
                        val wt = formWeight.toDoubleOrNull() ?: 0.0
                        if (wt in 20.0..300.0 && formDate <= todayLocal()) {
                            GlobalScope.launch {
                                // Find existing entry for this date to preserve sports + get ID
                                val existing = DataService.entries.find { it.date == formDate }
                                val existingSports = existing?.sports ?: emptyList()
                                val entryId = existing?.id
                                DataService.saveEntry(userId, formDate, wt, existingSports, formNote.ifBlank { existing?.note ?: "" }, entryId)
                                onSave()
                            }
                        }
                        Unit
                    }
                })
                appendChild(button(t("btn_cancel")) {
                    style.cssText = Styles.BTN_SECONDARY
                    onclick = { onSave(); Unit }
                })
            })
        }
    }

    // ── History list ────────────────────────────────────────────────────

    private fun renderHistory(entries: List<Entry>, userId: String, onRefresh: () -> Unit): HTMLElement {
        return div {
            style.cssText = Styles.CARD

            appendChild(div {
                style.cssText = "font-size:13px;text-transform:uppercase;letter-spacing:2px;color:${Styles.TEXT_40};font-weight:700;margin-bottom:16px"
                textContent = t("history_title")
            })

            val sorted = entries.sortedByDescending { it.date }
            if (sorted.isEmpty()) {
                appendChild(div {
                    style.cssText = "text-align:center;padding:24px;color:${Styles.TEXT_30};font-size:14px"
                    textContent = t("no_data_title")
                })
            } else {
                sorted.take(50).forEachIndexed { i, entry ->
                    val prev = if (i + 1 < sorted.size) sorted[i + 1] else null
                    val diff = if (prev != null && entry.weight > 0 && prev.weight > 0) entry.weight - prev.weight else null

                    appendChild(div {
                        style.cssText = "display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-bottom:1px solid ${Styles.SEPARATOR}"

                        appendChild(div {
                            style.cssText = "display:flex;align-items:center;gap:11px;flex:1;min-width:0"
                            // Sport icon or scale
                            val sportIcons = entry.sports.mapNotNull { s -> DataService.sports.find { it.k == s.k }?.i }.joinToString("")
                            appendChild(div {
                                style.cssText = "width:34px;height:34px;border-radius:10px;flex-shrink:0;background:${if (sportIcons.isNotBlank()) "${Styles.A1}15" else "${Styles.TEXT_04}"};display:flex;align-items:center;justify-content:center;font-size:15px"
                                textContent = sportIcons.ifBlank { "\u2696\uFE0F" }
                            })
                            appendChild(div {
                                style.cssText = "flex:1;min-width:0"
                                // Weight + change
                                appendChild(div {
                                    style.cssText = "display:flex;align-items:center;gap:8px;flex-wrap:wrap"
                                    if (entry.weight > 0) {
                                        appendChild(span("${entry.weight} kg") {
                                            style.cssText = "font-size:20px;font-weight:800;color:${Styles.TEXT}"
                                        })
                                    }
                                    if (diff != null) {
                                        val diffCol = if (diff < 0) Styles.A1 else if (diff > 0) Styles.PINK else "${Styles.TEXT_30}"
                                        appendChild(span("${if (diff > 0) "+" else ""}${fmt1(diff)}") {
                                            style.cssText = "font-size:14px;font-weight:700;color:$diffCol"
                                        })
                                    }
                                })
                                // Date
                                appendChild(div {
                                    style.cssText = "font-size:14px;color:${Styles.TEXT_SEC};font-weight:500"
                                    textContent = formatDateFull(entry.date)
                                })
                                // Sports detail
                                entry.sports.forEach { s ->
                                    val sp = DataService.sports.find { it.k == s.k }
                                    if (sp != null) {
                                        appendChild(div {
                                            style.cssText = "font-size:12px;color:${Styles.A1}88;font-weight:600;margin-top:2px"
                                            textContent = "${sp.i} ${sp.localName()} ${s.`val`.toInt()} ${I18n.tU(s.mode.ifBlank { sp.u })}"
                                        })
                                    }
                                }
                            })
                        })

                        // Delete button
                        appendChild(button("x") {
                            style.cssText = "background:none;border:none;cursor:pointer;color:${Styles.TEXT_10};font-size:16px;padding:2px 6px"
                            onclick = {
                                GlobalScope.launch {
                                    DataService.deleteEntry(entry.id)
                                    DataService.loadEntries(userId)
                                    onRefresh()
                                }
                            }
                        })
                    })
                }
            }
        }
    }

    // ── Helper functions ────────────────────────────────────────────────

    private fun calcStreak(entries: List<Entry>): Int {
        val we = entries.filter { it.weight > 0 }.sortedByDescending { it.date }
        if (we.isEmpty()) return 0
        val today = todayLocal()
        val latest = we.first().date
        if (daysBetween(latest, today) > 1) return 0
        var streak = 1
        for (i in 1 until we.size) {
            if (daysBetween(we[i].date, we[i - 1].date) <= 1) streak++
            else break
        }
        return streak
    }

    private fun calcTrend(entries: List<Entry>): Double? {
        val we = entries.filter { it.weight > 0 }.sortedBy { it.date }
        if (we.size < 3) return null
        val n = we.size
        val xM = (n - 1) / 2.0
        val yM = we.sumOf { it.weight } / n
        var num = 0.0; var den = 0.0
        for (i in 0 until n) {
            num += (i - xM) * (we[i].weight - yM)
            den += (i - xM) * (i - xM)
        }
        return if (den != 0.0) num / den else 0.0
    }

    private fun getBmiInfo(bmi: Double): Pair<String, String> {
        if (DataService.bmiZones.isNotEmpty()) {
            for (z in DataService.bmiZones) {
                if (bmi < z.maxBmi) return Pair(t(z.translationKey), Styles.adaptColor(z.color))
            }
            val last = DataService.bmiZones.last()
            return Pair(t(last.translationKey), Styles.adaptColor(last.color))
        }
        return when {
            bmi < 18.5 -> Pair(t("bmi_zone_underweight"), Styles.adaptColor("#38bdf8"))
            bmi < 25 -> Pair(t("bmi_zone_normal"), Styles.adaptColor("#2dd4bf"))
            bmi < 30 -> Pair(t("bmi_zone_overweight"), Styles.YELLOW)
            else -> Pair(t("bmi_zone_obese"), Styles.adaptColor("#fb7185"))
        }
    }
}
